import {
  BUSINESS_TYPE,
  HTTP_STATUS_CODES,
  INTENTS,
  MESSAGES,
  NATIONALITY_MAPPER,
  PAGE_ROUTES,
  PLAN_TYPES,
} from "@/config/constants";
import { URLS } from "@/config/urls";
import { setErrorData } from "@/features/error/errorSlice";
import {
  getMetadata,
  setCustomerData,
} from "@/features/metadata/metadataSlice";
import {
  getActiveState,
  getFromOrderTracking,
  getIntent,
  getIsEndorsementDone,
  getPreviousProposalDetails,
  getProposal,
  setGeneratedProposalNo,
  setIntent,
  setIsEndorsementDone,
  setIsEndorsementFieldEditable,
  setProposalId,
  setRefetchProposal,
} from "@/features/proposal/proposalSlice";
import {
  getBusinessType,
  getImmutablePreExistingIllness,
  getIsEmployee,
  getPayload,
  getQuoteAdditionalInfoData,
  getQuoteDetails,
  getQuotesData,
  getRebookCancelPolicy,
  getSelectedQuote,
  setMembersNationality,
  setPayload,
  setPreExistingIllness,
  setQuoteAdditionalInfoData,
  setQuoteError,
  setQuotesData,
  setSelectedAddOn,
  setSelectedPackage,
  setShowExistingIllnessBlock,
  setSkipAutoReviseQuote,
  setsuperchargeRiderLoading,
  updateDiscounts,
} from "@/features/quote/quoteSlice";
import axiosInstance from "@/middleware/axios-wrapper";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { isEmpty } from "lodash";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

const BackButton = ({ sx, backFunction }) => {
  const router = useRouter();
  const payload = useSelector(getPayload);
  const dispatch = useDispatch();
  const quotes = useSelector(getQuotesData);
  const quoteDetails = useSelector(getQuoteDetails);
  const clientId = quotes?.clientId;
  const metadata = useSelector(getMetadata);
  const lob = metadata?.lob;
  const intent = useSelector(getIntent) || metadata?.intent;
  const isEndorsementPolicy = intent === INTENTS.ENDORSEMENT;
  const previousProposalDetails = useSelector(getPreviousProposalDetails);
  const prevDetails = previousProposalDetails;
  const endorsementStatus = useSelector(getIsEndorsementDone);
  const isFromOrderTracking = useSelector(getFromOrderTracking);
  const activeStep = useSelector(getActiveState);
  const additionalQuoteData = useSelector(getQuoteAdditionalInfoData);
  const immutablePreExistingIllness = useSelector(
    getImmutablePreExistingIllness
  );
  const proposal = useSelector(getProposal);
  const rebookCancelPolicy = useSelector(getRebookCancelPolicy);
  const selectedQuote = useSelector(getSelectedQuote);
  const isEmployee = useSelector(getIsEmployee);
  const businessType = useSelector(getBusinessType);

  const fetchQuotes = useCallback(async () => {
    let updatedPayload = { ...payload };
    const memberData = [...updatedPayload.memberDetails];
    const sumInsured = quoteDetails?.sumInsured;
    const deductible = quoteDetails?.deductible;
    let updatedMember = [];
    memberData.forEach((ele) => {
      let obj = JSON.parse(JSON.stringify(ele));
      obj.sumInsured = Number(sumInsured);
      obj.deductible = Number(deductible);
      obj.preExistingIllness = immutablePreExistingIllness.includes(
        ele.relationship
      );
      // Reset residential status and GMC member status to defaults
      obj.residentialStatus = "Indian";
      obj.isGmcMember = true;
      updatedMember.push(obj);
    });
    updatedPayload.tenure = quoteDetails?.tenure;
    const isFloater = updatedPayload?.planType === PLAN_TYPES.FLOATER;
    const fedValues =
      updatedPayload["memberFED"]?.map((m) =>
        typeof m.fedValue !== "undefined" ? Number(m.fedValue) : null
      ) ?? [];

    const maxFedValue = fedValues.length
      ? Math.max(...fedValues.filter((v) => v !== null))
      : null;

    const discountsFED =
      proposal?.memberDetails?.map((m) => {
        const discount = m.premium?.favourableExperienceDiscountPercentage;
        return typeof discount !== "undefined" ? discount : null;
      }) ?? [];

    const maxFedDiscount = discountsFED.length
      ? Math.max(...discountsFED.filter((v) => v !== null))
      : null;
    if (!isEmpty(rebookCancelPolicy)) {
      // for fresh book case
      updatedMember = updatedMember?.map((member) => {
        const fedValue = updatedPayload["memberFED"]?.find(
          (FEDMember) => member.relationship === FEDMember.relationship
        )?.fedValue;
        const fedDiscountMember = proposal?.memberDetails?.find(
          (newMember) => member.relationship === newMember.relationship
        );
        const fedDiscount =
          fedDiscountMember?.premium &&
          typeof fedDiscountMember.premium
            .favourableExperienceDiscountPercentage !== "undefined"
            ? fedDiscountMember.premium.favourableExperienceDiscountPercentage
            : null;

        let optedFavourableExperienceDiscountPercentage;
        if (isFloater) {
          optedFavourableExperienceDiscountPercentage = isEmpty(fedValue)
            ? maxFedDiscount
            : maxFedValue;
        } else {
          optedFavourableExperienceDiscountPercentage = isEmpty(fedValue)
            ? fedDiscount
            : Number(fedValue);
        }
        return {
          ...member,
          optedFavourableExperienceDiscountPercentage,
        };
      });
    }
    updatedPayload.memberDetails = updatedMember;
    updatedPayload.clientId = clientId;
    updatedPayload.loginEmail = metadata?.loginEmail || "";
    let additionalDiscount = {
      professional: {
        opted: false,
        eligible: true,
      },
      employee: {
        opted: false,
        eligible: true,
      },
      lieuOfCommission: {
        opted: false,
        optedPercentage: "5",
      },
    };
    updatedPayload.additionalDiscount = additionalDiscount;

    // Update Redux payload with reset residential status and GMC member status
    dispatch(setPayload(updatedPayload));

    // Reset membersNationality to defaults (all members to "Indian")
    const resetNationality = {};
    updatedMember.forEach((member) => {
      resetNationality[member.relationship] = "Indian";
    });

    // Set flag to skip auto-trigger of REVISE_QUOTE when we reset nationality
    dispatch(setSkipAutoReviseQuote(true));
    dispatch(setMembersNationality(resetNationality));
    dispatch(setSelectedAddOn({}));

    dispatch(
      updateDiscounts({
        professionalDiscountOpted: false,
        employeeDiscountOpted: false,
        lieuOfCommissionOpted: false,
      })
    );
    dispatch(setsuperchargeRiderLoading(0));
    dispatch(
      setShowExistingIllnessBlock(!isEmpty(immutablePreExistingIllness))
    );
    dispatch(setPreExistingIllness(immutablePreExistingIllness));

    const cancelAndRebookMembers = updatedMember.map((member) => {
      const fedDiscountMember = proposal?.memberDetails?.find(
        (newMember) => member.relationship === newMember.relationship
      );
      const fedDiscount =
        fedDiscountMember?.premium &&
        typeof fedDiscountMember.premium
          .favourableExperienceDiscountPercentage !== "undefined"
          ? fedDiscountMember.premium.favourableExperienceDiscountPercentage
          : null;

      const FedValue = updatedPayload["memberFED"]?.find(
        (FEDMember) => member?.relationship === FEDMember?.relationship
      )?.fedValue;

      let optedFavourableExperienceDiscountPercentage;
      if (isFloater) {
        optedFavourableExperienceDiscountPercentage = isEmpty(FedValue)
          ? maxFedDiscount
          : maxFedValue;
      } else {
        optedFavourableExperienceDiscountPercentage = isEmpty(FedValue)
          ? fedDiscount
          : Number(FedValue);
      }
      return {
        sumInsured: member.sumInsured,
        dob: member.dob,
        gender: member.gender,
        relationship: member.relationship,
        loadingPercentage: 0,
        expiringSI: "",
        claimDetails: {
          claimsInLastYear: 0,
          claimsIn2ndLastYear: 0,
          claimsIn3rdLastYear: 0,
        },
        optedFavourableExperienceDiscountPercentage,
        residentialStatus: "Indian",
        isGmcMember: true,
      };
    });

    let newPayload = {
      source: payload.source,
      premium: 0,
      planType: updatedPayload.planType,
      tenure: quoteDetails?.tenure,
      isEmployee,
      goGreen: false,
      pincode:
        payload?.proposer?.address?.pincode ||
        payload?.pincode ||
        updatedPayload?.proposer?.address?.pincode ||
        updatedPayload?.pincode ||
        "",
      branchLocation: payload?.branchLocation,
      producerCode: payload?.producerCode,
      productCode: isEmpty(proposal?.secondaryProductCode)
        ? selectedQuote?.productCode
        : proposal?.secondaryProductCode,
      prevPolicyEndDate: proposal?.prevPolicyEndDate,
      memberDetails: cancelAndRebookMembers,
      packages: isEmpty(proposal?.packages)
        ? []
        : Object?.entries(proposal?.packages)
            .filter(([key, value]) => value.isOpted)
            .map(([key]) => key) || [],
      isMigrationPolicy: false,
      expiringZone: "",
      bypassMandatoryPackages: false,
      addonCovers: proposal?.premium?.addonCovers || {},
      isPolicyClaimed: false,
      crossSellId: "",
      noOfTenureCoveredWithTagic:
        proposal?.renewalSummary?.noOfTenureCoveredWithTagic || 0,
      PED: false, // TO DO: medical
      portability: businessType === BUSINESS_TYPE.PORT,
      byPassPackages: false,
      isSplitPolicy: false,
      proposer: {
        nationality: NATIONALITY_MAPPER[proposal?.proposer?.nationality],
      },
      adRiderFlag: proposal?.addonCovers?.["C005"]?.opted || false,
      packageVersion: "",
      rebookPolicyProcessType: "Rebook Policy",
      rebookPolicyStartDate: payload?.rebookPolicyStartDate,
      additionalDiscount: updatedPayload.additionalDiscount,
      lob: lob,
    };
    const { memberFED, ...payloadWithoutMemberFED } = updatedPayload; // removing memberFED from finalPayload

    try {
      const quoteResponse =
        // Rebook Case for Renewal Policy
        !isEmpty(rebookCancelPolicy) &&
        proposal?.businessType === BUSINESS_TYPE.RENEWAL
          ? await axiosInstance.post(URLS.CREATE_QUOTE_RENEWAL, newPayload)
          : // New Business Quote Creation
            await axiosInstance.post(
              URLS.CREATE_QUOTE,
              payloadWithoutMemberFED
            );

      if (quoteResponse.statusCode.toString() === HTTP_STATUS_CODES.OK.toString()) {
        if (isEmpty(quoteResponse.data)) {
          dispatch(
            setQuoteError({ hasError: true, error: quoteResponse.message })
          );
        } else {
          dispatch(setQuotesData(quoteResponse.data));
          dispatch(
            setQuoteAdditionalInfoData({
              zone: additionalQuoteData?.originalZone,
            })
          );
          localStorage.removeItem("zone");
          dispatch(setSelectedPackage({})); // TO be Change in we are showing mandatory packages selected in PDP screen
        }
      } else {
        dispatch(
          setQuoteError({ hasError: true, error: quoteResponse.message })
        );
        dispatch(
          setErrorData({
            show: true,
            msg: quoteResponse?.data || MESSAGES.DEFAULT_MESSAGE,
            status: "",
          })
        );
      }
    } catch (err) {
      dispatch(
        setQuoteError({ hasError: true, error: MESSAGES.NO_DATA_FOUND })
      );
      dispatch(
        setErrorData({
          show: true,
          msg: MESSAGES.DEFAULT_MESSAGE,
          status: err?.status,
        })
      );
    } finally {
      // Reset flag after API call completes (success or error)
      dispatch(setSkipAutoReviseQuote(false));
    }
  }, [
    businessType,
    clientId,
    dispatch,
    immutablePreExistingIllness,
    isEmployee,
    lob,
    metadata?.loginEmail,
    payload,
    proposal?.addonCovers,
    proposal?.businessType,
    proposal?.memberDetails,
    proposal?.packages,
    proposal?.premium?.addonCovers,
    proposal?.prevPolicyEndDate,
    proposal?.proposer?.nationality,
    proposal?.renewalSummary?.noOfTenureCoveredWithTagic,
    proposal?.secondaryProductCode,
    quoteDetails?.deductible,
    quoteDetails?.sumInsured,
    quoteDetails?.tenure,
    rebookCancelPolicy,
    selectedQuote?.productCode,
  ]);
  return (
    router.pathname !== PAGE_ROUTES.MEMBER_PAGE && (
      <ArrowBackIosNewIcon
        onClick={() => {
          if (router.pathname === PAGE_ROUTES.PDP) {
            fetchQuotes();
            router.back();
          } else if (
            isEndorsementPolicy &&
            router.pathname === PAGE_ROUTES.PROPOSAL
          ) {
            activeStep > 0
              ? backFunction()
              : (dispatch(setRefetchProposal(true)),
                dispatch(setGeneratedProposalNo(prevDetails?.proposalNo)),
                dispatch(setProposalId(prevDetails?.proposalId)),
                router.back());
          } else if (router.pathname === PAGE_ROUTES.POLICY_HISTORY) {
            dispatch(setRefetchProposal(true));
            dispatch(
              setGeneratedProposalNo(endorsementStatus?.currentEndorsmentNo)
            );
            dispatch(setProposalId(endorsementStatus?.currentEndorsementId));
            !isFromOrderTracking &&
              dispatch(
                setIsEndorsementDone({
                  isEndorsementDone: false,
                  isConsentDone: false,
                })
              );
            !isFromOrderTracking &&
              dispatch(
                setCustomerData({
                  intent: INTENTS.ENDORSEMENT,
                })
              );
            !isFromOrderTracking && dispatch(setIntent(INTENTS.ENDORSEMENT));
            // !isFromOrderTracking &&
            // dispatch(setIsFromEndorsementsActionsPage(true));
            !isFromOrderTracking &&
              dispatch(setIsEndorsementFieldEditable(true));
            router.back();
          } else {
            backFunction ? backFunction() : router.back();
          }
        }}
        sx={{
          cursor: "pointer",
          left: "-2.5rem",
          mt: "0.31rem",
          position: "absolute",
          display: { xs: "none", md: "block" },
          fontSize: "1.75em",
          ...sx,
        }}
      />
    )
  );
};

BackButton.propTypes = {
  backFunction: PropTypes.func,
  sx: PropTypes.object,
};

export default BackButton;
